/**
 * /alerts — 预警中心（Phase 5 §5.7）.
 * 列出本周 weekly_alerts，按严重性分组卡片化展示。支持认领 / 关闭。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Tag } from 'antd';

import ChipFilter from '@/components/ChipFilter';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import StatCard from '@/components/StatCard';
import { api } from '@/lib/api';
import { useWeekStore } from '@/stores/weekStore';

interface Alert {
  alert_id: number;
  report_week: string;
  alert_type: string;
  severity: '低' | '中' | '高' | '重大';
  brand_id: string | null;
  model_id: string | null;
  trigger_rule: string;
  trigger_value: number | null;
  message: string;
  status: string;
  owner: string | null;
  created_at: string;
}

const SEVERITY_OPTS = [
  { value: '', label: '全部' },
  { value: '重大', label: '重大' },
  { value: '高', label: '高' },
  { value: '中', label: '中' },
  { value: '低', label: '低' },
];

const STATUS_OPTS = [
  { value: '', label: '全部' },
  { value: '待处理', label: '待处理' },
  { value: '已认领', label: '已认领' },
  { value: '已关闭', label: '已关闭' },
];

const SEVERITY_COLOR: Record<Alert['severity'], { bg: string; fg: string }> = {
  重大: { bg: '#FEE2E2', fg: '#991B1B' },
  高: { bg: '#FFEDD5', fg: '#9A3412' },
  中: { bg: '#FEF3C7', fg: '#92400E' },
  低: { bg: '#DCFCE7', fg: '#15803D' },
};

export default function AlertsPage() {
  const { week } = useWeekStore();
  const qc = useQueryClient();
  const [severity, setSeverity] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', week, severity, statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = { week };
      if (severity) params.severity = severity;
      if (statusFilter) params.status = statusFilter;
      return (await api.get<Alert[]>('/alerts', { params })).data;
    },
    retry: false,
  });

  const claim = useMutation({
    mutationFn: async (id: number) =>
      api.post(`/alerts/${id}/claim`, null, { params: { owner: 'me' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', week] }),
  });

  const close = useMutation({
    mutationFn: async (id: number) => api.post(`/alerts/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', week] }),
  });

  const alerts = data ?? [];
  const usingMock = !data && !isLoading;

  const counts = {
    重大: alerts.filter((a) => a.severity === '重大').length,
    高: alerts.filter((a) => a.severity === '高').length,
    中: alerts.filter((a) => a.severity === '中').length,
    pending: alerts.filter((a) => a.status === '待处理').length,
  };

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="alerts 接口暂不可用 — 显示空状态" />}

      <div>
        <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">预警中心 · {week}</h1>
        <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
          本周触发 {alerts.length} 条预警 · 待处理 {counts.pending} 条
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <StatCard title="重大" value={counts.重大} hint="critical" />
        <StatCard title="高" value={counts.高} />
        <StatCard title="中" value={counts.中} />
        <StatCard title="待处理" value={counts.pending} />
      </div>

      <Panel title="筛选">
        <div className="flex flex-col gap-3">
          <ChipFilter
            label="等级"
            value={severity}
            onChange={setSeverity}
            options={SEVERITY_OPTS}
          />
          <ChipFilter
            label="状态"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTS}
          />
        </div>
      </Panel>

      <div className="space-y-3">
        {isLoading && <div className="text-[12px] text-slate-500">加载中...</div>}
        {!isLoading && alerts.length === 0 && (
          <div className="text-[12px] text-slate-400 py-12 text-center">暂无预警</div>
        )}
        {alerts.map((a) => {
          const palette = SEVERITY_COLOR[a.severity] ?? { bg: '#E2E8F0', fg: '#475569' };
          return (
            <div
              key={a.alert_id}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-start gap-3"
            >
              <span
                className="px-2 py-1 rounded-full text-[11px] font-semibold shrink-0"
                style={{ background: palette.bg, color: palette.fg }}
              >
                {a.severity}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 mb-1">{a.message}</div>
                <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                  <Tag>{a.alert_type}</Tag>
                  {a.brand_id && <span>品牌：{a.brand_id}</span>}
                  {a.model_id && <span>· 车型：{a.model_id}</span>}
                  <span>· 规则：{a.trigger_rule}</span>
                  <Tag color={a.status === '已关闭' ? 'green' : a.status === '已认领' ? 'blue' : 'orange'}>
                    {a.status}
                  </Tag>
                  {a.owner && <span>· 处理人：{a.owner}</span>}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {a.status === '待处理' && (
                  <Button size="small" onClick={() => claim.mutate(a.alert_id)}>
                    认领
                  </Button>
                )}
                {a.status !== '已关闭' && (
                  <Button
                    size="small"
                    type="primary"
                    danger
                    onClick={() => close.mutate(a.alert_id)}
                  >
                    关闭
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
